// report_builder/ReportList.jsx
// ============================================================
// REPORT LIST
// Main entry view for the Report Builder feature.
// Shows all saved reports with Run / Edit / Delete actions.
// Also contains the "New Report" button.
//
// Props:
//   onNew    — fn()         — open blank builder
//   onEdit   — fn(report)   — open builder with report loaded
//   onRun    — fn(report)   — open runner for this report
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  fetchReports, deleteReport,
  setReportLive, fetchWatchlistsForReport,
  duplicateReport, fetchReport, createReport,
} from './reportApi';

// ── Format timestamp to readable IST string ──────────────────
function fmtDate(isoStr) {
  if (!isoStr) return '—';
  const d   = new Date(isoStr);
  const ist = new Date(d.getTime() + 5.5 * 60 * 60 * 1000);
  const dd  = String(ist.getUTCDate()).padStart(2, '0');
  const mm  = String(ist.getUTCMonth() + 1).padStart(2, '0');
  const yy  = ist.getUTCFullYear();
  return `${dd}-${mm}-${yy}`;
}

// ── Count entry conditions in a report ───────────────────────
function countConditions(report) {
  const def   = report.definition || {};
  const conds = def.entry?.conditions || def.conditions || [];
  return conds.length;
}


// ============================================================
// REPORT LIST
// ============================================================

// ============================================================
// LIVE CONTROLS (R6) — toggle + watchlist per report card
// ============================================================
function LiveControls({ report, watchlists, onChanged,
                        onDuplicate, onExport }) {
  const [busy, setBusy] = useState(false);
  const live  = !!report.live_enabled;
  const wlId  = report.live_watchlist_id || '';

  const ls = {
    row:   { display: 'flex', alignItems: 'center', gap: '8px',
             marginTop: '8px', padding: '6px 8px',
             background: live ? '#e8f5e9' : '#f5f5f5',
             borderRadius: '6px', fontSize: '12px' },
    label: { display: 'flex', alignItems: 'center', gap: '4px',
             cursor: 'pointer', fontWeight: 600,
             color: live ? '#1b5e20' : '#666' },
    dot:   { width: '8px', height: '8px', borderRadius: '50%',
             background: live ? '#2e7d32' : '#bbb',
             display: 'inline-block' },
    select:{ fontSize: '12px', padding: '2px 4px', maxWidth: '150px',
             border: '1px solid #ccc', borderRadius: '4px' },
    err:   { color: '#c62828', fontSize: '11px' },
  };

  const apply = async (fields) => {
    setBusy(true);
    try {
      const res = await setReportLive(report.id, fields);
      onChanged(report.id, {
        live_enabled:      res.live_enabled,
        live_watchlist_id: res.live_watchlist_id,
      });
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Live update failed';
      window.alert(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={ls.row}>
      <span style={ls.dot} />
      <label style={ls.label}>
        <input
          type="checkbox"
          checked={live}
          disabled={busy}
          onChange={e => apply({ live_enabled: e.target.checked })}
        />
        Live
      </label>
      <select
        style={ls.select}
        value={wlId}
        disabled={busy}
        onChange={e => apply({
          live_watchlist_id: e.target.value ? Number(e.target.value) : null,
        })}
      >
        <option value="">— watchlist —</option>
        {(watchlists || []).map(wl => (
          <option key={wl.id} value={wl.id}>{wl.name}</option>
        ))}
      </select>
      {live && !wlId && (
        <span style={ls.err}>attach a watchlist</span>
      )}
      {/* R7 — card tools */}
      <span style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
        <button
          title="Duplicate report"
          style={{ border: '1px solid #ddd', background: '#fff',
                   borderRadius: '4px', cursor: 'pointer',
                   fontSize: '11px', padding: '2px 7px' }}
          onClick={() => onDuplicate(report.id)}
        >⧉</button>
        <button
          title="Export JSON"
          style={{ border: '1px solid #ddd', background: '#fff',
                   borderRadius: '4px', cursor: 'pointer',
                   fontSize: '11px', padding: '2px 7px' }}
          onClick={() => onExport(report)}
        >⇩</button>
      </span>
    </div>
  );
}


export default function ReportList({ onNew, onEdit, onRun }) {
  const [reports,       setReports]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [deleting,      setDeleting]      = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // ── Load reports ──────────────────────────────────────────
  const loadReports = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchReports()
      .then(data => {
        setReports(data || []);
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to load reports. Check backend.');
        setLoading(false);
      });
  }, []);

  useEffect(() => { loadReports(); }, [loadReports]);

  // ── R6: watchlists for the Live picker ────────────────────
  const [watchlists, setWatchlists] = useState([]);
  useEffect(() => {
    fetchWatchlistsForReport()
      .then(data => setWatchlists(data || []))
      .catch(() => setWatchlists([]));
  }, []);

  // ── R6: reflect a live change on the card without reload ──
  const handleLiveChange = (reportId, fields) => {
    setReports(prev => prev.map(r =>
      r.id === reportId ? { ...r, ...fields } : r));
  };

  // ── R7: duplicate / export / import ───────────────────────
  const handleDuplicate = async (reportId) => {
    try {
      await duplicateReport(reportId);
      loadReports();
    } catch {
      setError('Failed to duplicate report.');
    }
  };

  const handleExport = async (report) => {
    try {
      const full = await fetchReport(report.id);
      const payload = {
        name:           full.name,
        description:    full.description,
        base_timeframe: full.base_timeframe,
        definition:     full.definition,
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)],
                            { type: 'application/json' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `report_${(full.name || 'export')
                     .replace(/[^a-z0-9_-]/gi, '_')}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Failed to export report.');
    }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';                       // allow re-import same file
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (!data.definition) {
        setError('Invalid file — no definition found.');
        return;
      }
      await createReport({
        name:           `${data.name || 'Imported report'} (imported)`,
        description:    data.description || null,
        base_timeframe: data.base_timeframe || null,
        definition:     data.definition,
      });
      loadReports();
    } catch {
      setError('Import failed — is the file a valid report JSON?');
    }
  };

  // ── Delete ────────────────────────────────────────────────
  const handleDeleteConfirm = async (reportId) => {
    setDeleting(reportId);
    setDeleteConfirm(null);
    try {
      await deleteReport(reportId);
      setReports(prev => prev.filter(r => r.id !== reportId));
    } catch {
      setError('Failed to delete report.');
    } finally {
      setDeleting(null);
    }
  };


  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div style={s.container}>

      {/* ── Header ── */}
      <div style={s.header}>
        <span style={s.headerTitle}>Report Builder</span>
        <div style={{ display: 'flex', gap: '8px' }}>
          <label style={{
            padding: '5px 12px', fontSize: '12px', cursor: 'pointer',
            background: '#f0f0f0', border: '1px solid #ddd',
            borderRadius: '4px', color: '#444',
          }}>
            ⇪ Import JSON
            <input
              type="file"
              accept=".json,application/json"
              style={{ display: 'none' }}
              onChange={handleImportFile}
            />
          </label>
          <button style={s.btnNew} onClick={onNew}>
            + New Report
          </button>
        </div>
      </div>

      {/* ── Body ── */}
      <div style={s.body}>

        {loading && (
          <div style={s.centreMsg}>Loading reports...</div>
        )}

        {error && (
          <div style={s.errorBox}>
            {error}
            <button style={s.msgClose} onClick={() => setError(null)}>✕</button>
          </div>
        )}

        {!loading && !error && reports.length === 0 && (
          <div style={s.emptyState}>
            <div style={s.emptyIcon}>📋</div>
            <div style={s.emptyTitle}>No reports yet</div>
            <div style={s.emptyHint}>
              Click <strong>+ New Report</strong> to build your first strategy report.
            </div>
            <button style={s.btnNewLarge} onClick={onNew}>
              + New Report
            </button>
          </div>
        )}

        {!loading && reports.length > 0 && (
          <div style={s.cardGrid}>
            {reports.map(report => (
              <div key={report.id} style={s.card}>

                {/* Card header */}
                <div style={s.cardHeader}>
                  <span style={s.cardName}>{report.name}</span>
                  <span style={s.cardTf}>
                    {report.base_timeframe || '—'}
                  </span>
                </div>

                {/* Description */}
                {report.description && (
                  <div style={s.cardDesc}>{report.description}</div>
                )}

                {/* Live toggle + watchlist (R6) + tools (R7) */}
                <LiveControls
                  report={report}
                  watchlists={watchlists}
                  onChanged={handleLiveChange}
                  onDuplicate={handleDuplicate}
                  onExport={handleExport}
                />

                {/* Meta */}
                <div style={s.cardMeta}>
                  <span style={s.cardMetaItem}>
                    {countConditions(report)} condition{countConditions(report) !== 1 ? 's' : ''}
                  </span>
                  <span style={s.cardMetaDot}>·</span>
                  <span style={s.cardMetaItem}>
                    Created {fmtDate(report.created_at)}
                  </span>
                  {report.updated_at !== report.created_at && (
                    <>
                      <span style={s.cardMetaDot}>·</span>
                      <span style={s.cardMetaItem}>
                        Updated {fmtDate(report.updated_at)}
                      </span>
                    </>
                  )}
                </div>

                {/* Actions */}
                <div style={s.cardActions}>
                  <button style={s.btnRun}  onClick={() => onRun(report)}>
                    ▶ Run
                  </button>
                  <button style={s.btnEdit} onClick={() => onEdit(report)}>
                    ✎ Edit
                  </button>

                  {deleteConfirm === report.id ? (
                    <div style={s.deleteConfirmRow}>
                      <span style={s.deleteConfirmText}>Delete?</span>
                      <button
                        style={s.btnDeleteConfirm}
                        onClick={() => handleDeleteConfirm(report.id)}
                        disabled={deleting === report.id}
                      >
                        {deleting === report.id ? '...' : 'Yes'}
                      </button>
                      <button
                        style={s.btnDeleteCancel}
                        onClick={() => setDeleteConfirm(null)}
                      >
                        No
                      </button>
                    </div>
                  ) : (
                    <button
                      style={s.btnDelete}
                      onClick={() => setDeleteConfirm(report.id)}
                    >
                      ✕ Delete
                    </button>
                  )}
                </div>

              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}


// ============================================================
// STYLES
// ============================================================
const s = {
  container: {
    display:       'flex',
    flexDirection: 'column',
    height:        '100%',
    overflow:      'hidden',
    background:    '#f7f8fa',
  },
  header: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'space-between',
    padding:        '10px 16px',
    background:     '#fff',
    borderBottom:   '1px solid #e0e0e0',
    flexShrink:     0,
  },
  headerTitle: {
    fontSize:   '14px',
    fontWeight: 'bold',
    color:      '#131722',
  },
  btnNew: {
    padding:         '6px 14px',
    fontSize:        '13px',
    backgroundColor: '#2962ff',
    color:           '#fff',
    border:          'none',
    borderRadius:    '4px',
    cursor:          'pointer',
    fontWeight:      'bold',
  },
  body: {
    flex:      1,
    overflowY: 'auto',
    padding:   '16px',
  },
  centreMsg: {
    textAlign: 'center',
    padding:   '40px',
    fontSize:  '13px',
    color:     '#888',
  },
  errorBox: {
    display:         'flex',
    justifyContent:  'space-between',
    alignItems:      'center',
    padding:         '10px 14px',
    backgroundColor: '#fff0f0',
    color:           '#d32f2f',
    border:          '1px solid #ffcdd2',
    borderRadius:    '4px',
    fontSize:        '13px',
    marginBottom:    '12px',
  },
  msgClose: {
    background: 'none',
    border:     'none',
    cursor:     'pointer',
    fontSize:   '14px',
    color:      'inherit',
  },
  emptyState: {
    display:        'flex',
    flexDirection:  'column',
    alignItems:     'center',
    justifyContent: 'center',
    padding:        '60px 20px',
    gap:            '12px',
  },
  emptyIcon:  { fontSize: '40px' },
  emptyTitle: { fontSize: '16px', fontWeight: 'bold', color: '#444' },
  emptyHint:  { fontSize: '13px', color: '#888', textAlign: 'center', maxWidth: '300px' },
  btnNewLarge: {
    padding:         '8px 20px',
    fontSize:        '13px',
    backgroundColor: '#2962ff',
    color:           '#fff',
    border:          'none',
    borderRadius:    '4px',
    cursor:          'pointer',
    fontWeight:      'bold',
    marginTop:       '8px',
  },
  cardGrid: {
    display:      'flex',
    flexWrap:     'wrap',
    gap:          '12px',
    alignContent: 'flex-start',
  },
  card: {
    background:    '#fff',
    border:        '1px solid #e0e0e0',
    borderRadius:  '6px',
    padding:       '14px 16px',
    width:         '320px',
    display:       'flex',
    flexDirection: 'column',
    gap:           '8px',
    boxShadow:     '0 1px 3px rgba(0,0,0,0.06)',
  },
  cardHeader: {
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'space-between',
    gap:            '8px',
  },
  cardName: {
    fontSize:   '14px',
    fontWeight: 'bold',
    color:      '#131722',
    flex:       1,
  },
  cardTf: {
    fontSize:        '11px',
    fontWeight:      'bold',
    color:           '#2962ff',
    backgroundColor: '#e8f0fe',
    padding:         '2px 7px',
    borderRadius:    '10px',
    flexShrink:      0,
  },
  cardDesc: {
    fontSize:   '12px',
    color:      '#666',
    lineHeight: '1.4',
  },
  cardMeta: {
    display:    'flex',
    alignItems: 'center',
    gap:        '4px',
    flexWrap:   'wrap',
  },
  cardMetaItem: { fontSize: '11px', color: '#aaa' },
  cardMetaDot:  { fontSize: '11px', color: '#ddd' },
  cardActions: {
    display:    'flex',
    alignItems: 'center',
    gap:        '6px',
    marginTop:  '4px',
  },
  btnRun: {
    padding:         '5px 12px',
    fontSize:        '12px',
    backgroundColor: '#2962ff',
    color:           '#fff',
    border:          'none',
    borderRadius:    '4px',
    cursor:          'pointer',
    fontWeight:      'bold',
  },
  btnEdit: {
    padding:         '5px 12px',
    fontSize:        '12px',
    backgroundColor: '#f0f0f0',
    color:           '#444',
    border:          '1px solid #ddd',
    borderRadius:    '4px',
    cursor:          'pointer',
  },
  btnDelete: {
    padding:         '5px 10px',
    fontSize:        '12px',
    backgroundColor: '#fff',
    color:           '#d32f2f',
    border:          '1px solid #ffcdd2',
    borderRadius:    '4px',
    cursor:          'pointer',
    marginLeft:      'auto',
  },
  deleteConfirmRow: {
    display:    'flex',
    alignItems: 'center',
    gap:        '4px',
    marginLeft: 'auto',
  },
  deleteConfirmText: { fontSize: '12px', color: '#d32f2f' },
  btnDeleteConfirm: {
    padding:         '3px 8px',
    fontSize:        '11px',
    backgroundColor: '#d32f2f',
    color:           '#fff',
    border:          'none',
    borderRadius:    '3px',
    cursor:          'pointer',
  },
  btnDeleteCancel: {
    padding:         '3px 8px',
    fontSize:        '11px',
    backgroundColor: '#f0f0f0',
    color:           '#444',
    border:          '1px solid #ddd',
    borderRadius:    '3px',
    cursor:          'pointer',
  },
};